import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { useBranding, logoUrl } from "../contexts/BrandingContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { PageHeader, Field } from "../components/Shared";
import { ImageUpload } from "../components/ImageUpload";
import { BRAND } from "../constants/testIds";
import { Plus, Trash2 } from "lucide-react";

const EMPTY_BI = { id: "", en: "" };
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

export default function Branding() {
  const { t } = useLang();
  const { reload } = useBranding();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/branding").then(({ data }) => {
      const d = data.data;
      setForm({
        app_name: d.app_name || "",
        tagline: { ...EMPTY_BI, ...(d.tagline || {}) },
        logo_file_id: d.logo_file_id || null,
        accent_color: d.accent_color || "#FF3B30",
        hero_eyebrow: { ...EMPTY_BI, ...(d.hero_eyebrow || {}) },
        hero_title: { ...EMPTY_BI, ...(d.hero_title || {}) },
        hero_body: { ...EMPTY_BI, ...(d.hero_body || {}) },
        steps: (d.steps || []).map((s) => ({ _key: uid(), title: { ...EMPTY_BI, ...(s.title || {}) }, body: { ...EMPTY_BI, ...(s.body || {}) } })),
        faq: (d.faq || []).map((f) => ({ _key: uid(), q: { ...EMPTY_BI, ...(f.q || {}) }, a: { ...EMPTY_BI, ...(f.a || {}) } })),
        contact_whatsapp: d.contact_whatsapp || "",
        contact_email: d.contact_email || "",
        socials: { instagram: "", tiktok: "", youtube: "", facebook: "", ...(d.socials || {}) },
      });
    }).catch((e) => toast.error(extractError(e).message));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/branding", form);
      toast.success(t("branding.saved"));
      reload();
    } catch (err) { toast.error(extractError(err).message); }
    finally { setBusy(false); }
  };

  if (!form) return <p className="text-slate-400">{t("common.loading")}</p>;

  const bi = (key, label) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label={`${label} (ID)`} testId={`branding-${key}-id`} value={form[key].id || ""} onChange={(e) => setForm({ ...form, [key]: { ...form[key], id: e.target.value } })} />
      <Field label={`${label} (EN)`} testId={`branding-${key}-en`} value={form[key].en || ""} onChange={(e) => setForm({ ...form, [key]: { ...form[key], en: e.target.value } })} />
    </div>
  );

  const setList = (key, i, sub, lang, value) => {
    const list = [...form[key]];
    list[i] = { ...list[i], [sub]: { ...list[i][sub], [lang]: value } };
    setForm({ ...form, [key]: list });
  };

  return (
    <form data-testid={BRAND.page} onSubmit={save} className="space-y-8">
      <PageHeader eyebrow={t("branding.eyebrow")} title={t("branding.title")} subtitle={t("branding.subtitle")}>
        <Button data-testid={BRAND.saveBtn} type="submit" disabled={busy} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white h-11 font-semibold">
          {busy ? t("common.loading") : t("branding.save")}
        </Button>
      </PageHeader>

      <Card className="bg-[#161923] border-white/5">
        <CardContent className="p-6 space-y-5">
          <h2 className="text-base md:text-lg font-semibold text-white">{t("branding.identity")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("branding.appName")} testId={BRAND.appName} value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} placeholder="Fat Loss Program" />
            <div className="space-y-1.5">
              <label className="text-slate-300 text-sm">{t("branding.accent")}</label>
              <div className="flex gap-2">
                <input data-testid={BRAND.accent} type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-10 w-14 rounded-md bg-[#1C202E] border border-white/10" />
                <input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="flex-1 h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3 font-mono" />
              </div>
            </div>
          </div>
          {bi("tagline", t("branding.tagline"))}
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            {form.logo_file_id && <img data-testid={BRAND.logoPreview} src={logoUrl(form.logo_file_id)} alt="logo" className="h-14 w-14 rounded-lg object-contain bg-white/5 border border-white/10 p-1" />}
            <div className="flex-1"><ImageUpload endpoint="/uploads/branding-logo" testId={BRAND.logoUpload} label={t("branding.logo")} onUploaded={(id) => setForm({ ...form, logo_file_id: id })} /></div>
            {form.logo_file_id && (
              <Button type="button" data-testid={BRAND.logoRemove} variant="ghost" onClick={() => setForm({ ...form, logo_file_id: null })} className="text-red-400 hover:bg-red-500/10 h-10">{t("branding.removeLogo")}</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#161923] border-white/5">
        <CardContent className="p-6 space-y-5">
          <h2 className="text-base md:text-lg font-semibold text-white">{t("branding.hero")}</h2>
          {bi("hero_eyebrow", t("branding.heroEyebrow"))}
          {bi("hero_title", t("branding.heroTitle"))}
          {bi("hero_body", t("branding.heroBody"))}
          <p className="text-xs text-slate-500">{t("branding.fallbackHint")}</p>
        </CardContent>
      </Card>

      <Card className="bg-[#161923] border-white/5">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base md:text-lg font-semibold text-white">{t("branding.steps")}</h2>
            <Button type="button" data-testid={BRAND.addStep} variant="ghost" onClick={() => setForm({ ...form, steps: [...form.steps, { _key: uid(), title: { ...EMPTY_BI }, body: { ...EMPTY_BI } }] })} className="text-slate-300 hover:bg-white/5 h-9">
              <Plus className="h-4 w-4 mr-1" /> {t("branding.addItem")}
            </Button>
          </div>
          {form.steps.map((s, i) => (
            <div key={s._key} data-testid={`branding-step-${i}`} className="rounded-xl border border-white/5 p-4 space-y-3">
              <div className="flex justify-between items-center text-xs text-slate-500">
                <span>#{i + 1}</span>
                <Button type="button" data-testid={`branding-step-remove-${i}`} variant="ghost" onClick={() => setForm({ ...form, steps: form.steps.filter((_, x) => x !== i) })} className="text-red-400 hover:bg-red-500/10 h-8"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={`${t("branding.stepTitle")} (ID)`} testId={`branding-step-${i}-title-id`} value={s.title.id || ""} onChange={(e) => setList("steps", i, "title", "id", e.target.value)} />
                <Field label={`${t("branding.stepTitle")} (EN)`} testId={`branding-step-${i}-title-en`} value={s.title.en || ""} onChange={(e) => setList("steps", i, "title", "en", e.target.value)} />
                <Field label={`${t("branding.stepBody")} (ID)`} testId={`branding-step-${i}-body-id`} value={s.body.id || ""} onChange={(e) => setList("steps", i, "body", "id", e.target.value)} />
                <Field label={`${t("branding.stepBody")} (EN)`} testId={`branding-step-${i}-body-en`} value={s.body.en || ""} onChange={(e) => setList("steps", i, "body", "en", e.target.value)} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-[#161923] border-white/5">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base md:text-lg font-semibold text-white">{t("branding.faq")}</h2>
            <Button type="button" data-testid={BRAND.addFaq} variant="ghost" onClick={() => setForm({ ...form, faq: [...form.faq, { _key: uid(), q: { ...EMPTY_BI }, a: { ...EMPTY_BI } }] })} className="text-slate-300 hover:bg-white/5 h-9">
              <Plus className="h-4 w-4 mr-1" /> {t("branding.addItem")}
            </Button>
          </div>
          {form.faq.map((f, i) => (
            <div key={f._key} data-testid={`branding-faq-${i}`} className="rounded-xl border border-white/5 p-4 space-y-3">
              <div className="flex justify-between items-center text-xs text-slate-500">
                <span>#{i + 1}</span>
                <Button type="button" data-testid={`branding-faq-remove-${i}`} variant="ghost" onClick={() => setForm({ ...form, faq: form.faq.filter((_, x) => x !== i) })} className="text-red-400 hover:bg-red-500/10 h-8"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={`${t("branding.question")} (ID)`} testId={`branding-faq-${i}-q-id`} value={f.q.id || ""} onChange={(e) => setList("faq", i, "q", "id", e.target.value)} />
                <Field label={`${t("branding.question")} (EN)`} testId={`branding-faq-${i}-q-en`} value={f.q.en || ""} onChange={(e) => setList("faq", i, "q", "en", e.target.value)} />
                <Field label={`${t("branding.answer")} (ID)`} testId={`branding-faq-${i}-a-id`} value={f.a.id || ""} onChange={(e) => setList("faq", i, "a", "id", e.target.value)} />
                <Field label={`${t("branding.answer")} (EN)`} testId={`branding-faq-${i}-a-en`} value={f.a.en || ""} onChange={(e) => setList("faq", i, "a", "en", e.target.value)} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-[#161923] border-white/5">
        <CardContent className="p-6 space-y-4">
          <h2 className="text-base md:text-lg font-semibold text-white">{t("branding.contact")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("branding.whatsapp")} testId={BRAND.whatsapp} value={form.contact_whatsapp} onChange={(e) => setForm({ ...form, contact_whatsapp: e.target.value })} placeholder="+628..." />
            <Field label={t("branding.email")} testId={BRAND.email} type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            {["instagram", "tiktok", "youtube", "facebook"].map((s) => (
              <Field key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} testId={`branding-social-${s}`} value={form.socials[s] || ""} onChange={(e) => setForm({ ...form, socials: { ...form.socials, [s]: e.target.value } })} placeholder="https://" />
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={busy} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white h-11 font-semibold">{busy ? t("common.loading") : t("branding.save")}</Button>
      </div>
    </form>
  );
}
